import React, { useState, useEffect, useRef } from "react";

// 🧠 Appel de ton proxy PHP (Chatbase)
async function askGPT(question, history = []) {
  const res = await fetch("https://djatervoyage.com/backend_djatervoyage/ai/chatbase_proxy.php", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, history })
  });
  const data = await res.json();
  console.log("Réponse Chatbase:", data);
  return data.output?.text || data.text || "Désolé, je n'ai pas compris.";
}

export default function AITravelAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { 
      sender: "ai", 
      text: "Bonjour 👋 ! Je suis votre assistant voyage intelligent. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Suggestions rapides
  const quickSuggestions = [
    "📞 Comment vous contacter"
  ];

  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  // Focus sur l'input quand le chat s'ouvre
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = async (text = input) => {
    if (!text.trim() || loading) return;
    const question = text.trim();

    const newHistory = [...history, { role: "user", content: question }];
    setMessages((m) => [...m, { 
      sender: "user", 
      text: question,
      timestamp: new Date()
    }]);
    setInput("");
    setLoading(true);
    setTyping(true);

    try {
      const reply = await askGPT(question, newHistory);
      setTyping(false);
      
      // Effet d'écriture progressive
      setTimeout(() => {
        setMessages((m) => [...m, { 
          sender: "ai", 
          text: reply,
          timestamp: new Date()
        }]);
        setHistory([...newHistory, { role: "assistant", content: reply }]);
      }, 500);
    } catch (err) {
      setTyping(false);
      setMessages((m) => [
        ...m,
        { 
          sender: "ai", 
          text: "Désolé, une erreur est survenue. Veuillez réessayer dans quelques instants.",
          timestamp: new Date()
        },
      ]);
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const clearChat = () => {
    setMessages([
      { 
        sender: "ai", 
        text: "Conversation réinitialisée. Comment puis-je vous aider ?",
        timestamp: new Date()
      }
    ]);
    setHistory([]);
  };

  // Bouton flottant pour ouvrir le chat
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 hover:shadow-blue-500/50 animate-bounce"
        aria-label="Ouvrir le chat"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
          1
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-300 ${
        isMinimized ? 'h-16' : 'h-[500px]'
      }`}
      style={{
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)'
      }}
    >
      {/* Header avec gradient moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 text-white px-5 py-4 rounded-t-2xl font-bold flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
              </svg>
            </div>
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
          </div>
          <div>
            <div className="text-sm font-bold">Assistant Djater</div>
            <div className="text-xs opacity-90">En ligne • Répond en quelques secondes</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center transition-all"
            aria-label={isMinimized ? "Agrandir" : "Réduire"}
          >
            {isMinimized ? "▲" : "▼"}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center transition-all"
            aria-label="Fermer le chat"
          >
            ✕
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Zone de messages avec scroll personnalisé */}
          <div 
            className="flex-1 p-4 overflow-y-auto space-y-4 scroll-smooth"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 #f1f5f9'
            }}
          >
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
              >
                <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === "user" ? "flex-row-reverse" : "flex-row"}`}>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    msg.sender === "ai" 
                      ? "bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-md" 
                      : "bg-gradient-to-br from-gray-600 to-gray-700 text-white shadow-md"
                  }`}>
                    {msg.sender === "ai" ? "🤖" : "👤"}
                  </div>
                  
                  {/* Message bubble */}
                  <div className="flex flex-col">
                    <div className={`px-4 py-3 rounded-2xl shadow-md ${
                      msg.sender === "ai" 
                        ? "bg-gradient-to-br from-blue-50 to-blue-100 text-gray-800 rounded-bl-sm" 
                        : "bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-br-sm"
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                    </div>
                    <span className="text-xs text-gray-400 mt-1 px-2">
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Indicateur de frappe */}
            {typing && (
              <div className="flex justify-start animate-fade-in">
                <div className="flex items-end gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white flex items-center justify-center text-sm shadow-md">
                    🤖
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-4 py-3 rounded-2xl rounded-bl-sm shadow-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions rapides */}
          {messages.length === 1 && !loading && (
            <div className="px-4 pb-2">
              <div className="text-xs text-gray-500 mb-2 font-medium">Suggestions rapides :</div>
              <div className="flex flex-wrap gap-2">
                {quickSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-xs bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-700 px-3 py-2 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Zone de saisie moderne */}
          <div className="p-4 border-t border-gray-200 bg-gray-50/50 rounded-b-2xl">
            <div className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                  placeholder="Écrivez votre message..."
                  disabled={loading}
                  rows="1"
                  style={{ maxHeight: '100px', minHeight: '44px' }}
                />
                {messages.length > 1 && (
                  <button
                    onClick={clearChat}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all"
                    title="Nouvelle conversation"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                aria-label="Envoyer"
              >
                {loading ? (
                  <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
            <div className="text-xs text-gray-400 mt-2 text-center">
              Propulsé par l'IA • Appuyez sur Entrée pour envoyer
            </div>
          </div>
        </>
      )}
    </div>
  );
}
